import { useTheme } from '@/hooks/useTheme';

const industries = [
  {
    icon: 'ri-scales-3-line',
    name: 'Law Firms',
    detail: 'Intake automation, client communication, and case workflow management.',
    imageUrl: 'https://public.readdy.ai/ai/img_res/16defbb3e1c451ab0a051282cb6b75cb.jpg',
  },
  {
    icon: 'ri-calculator-line',
    name: 'Accounting Firms',
    detail: 'Document collection, deadline tracking, and client onboarding systems.',
    imageUrl: 'https://readdy.ai/api/search-image?query=modern%20accounting%20office%20financial%20data%20visualization%20screens%20clean%20professional%20workspace%20neutral%20tones%20white%20and%20gray%20sophisticated%20business%20environment&width=480&height=280&seq=industry-accounting-1&orientation=landscape',
  },
  {
    icon: 'ri-heart-pulse-line',
    name: 'Medical Practices',
    detail: 'Appointment flows, patient communication, and administrative automation.',
    imageUrl: 'https://readdy.ai/api/search-image?query=modern%20medical%20clinic%20interior%20clean%20white%20clinical%20environment%20minimal%20healthcare%20design%20professional%20medical%20practice%20reception%20area%20sophisticated%20contemporary&width=480&height=280&seq=industry-medical-1&orientation=landscape',
  },
  {
    icon: 'ri-server-line',
    name: 'IT Service Providers',
    detail: 'Ticketing systems, SLA tracking, and client escalation workflows.',
    imageUrl: 'https://readdy.ai/api/search-image?query=modern%20IT%20server%20room%20data%20center%20blue%20glowing%20lights%20rack%20servers%20clean%20minimal%20technology%20environment%20professional%20enterprise%20infrastructure&width=480&height=280&seq=industry-it-1&orientation=landscape',
  },
  {
    icon: 'ri-building-2-line',
    name: 'Real Estate Firms',
    detail: 'Lead qualification, follow-up sequences, and transaction coordination.',
    imageUrl: 'https://readdy.ai/api/search-image?query=modern%20luxury%20real%20estate%20office%20interior%20sleek%20minimal%20design%20floor%20to%20ceiling%20windows%20city%20view%20sophisticated%20professional%20workspace%20neutral%20tones&width=480&height=280&seq=industry-realestate-1&orientation=landscape',
  },
  {
    icon: 'ri-megaphone-line',
    name: 'Marketing Agencies',
    detail: 'Client reporting, campaign coordination, and delivery automation.',
    imageUrl: 'https://readdy.ai/api/search-image?query=modern%20creative%20marketing%20agency%20office%20vibrant%20workspace%20screens%20showing%20campaign%20analytics%20clean%20contemporary%20design%20open%20workspace%20professional%20environment&width=480&height=280&seq=industry-marketing-1&orientation=landscape',
  },
];

export default function IndustriesSection() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <section id="industries" className="py-28 transition-colors duration-500" style={{ background: isDark ? '#0E1116' : '#F5F5F7' }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-3 mb-6">
            <div className={`w-8 h-px ${isDark ? 'bg-violet-400/50' : 'bg-purple-500/50'}`}></div>
            <span className={`text-xs tracking-widest uppercase font-medium ${isDark ? 'text-violet-400' : 'text-purple-500'}`}>Industries</span>
            <div className={`w-8 h-px ${isDark ? 'bg-violet-400/50' : 'bg-purple-500/50'}`}></div>
          </div>
          <h2 className={`text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-tight mb-6 ${isDark ? 'text-white' : 'text-[#1d1d1f]'}`}>
            Designed for{' '}
            <span className="text-gradient-violet">Operationally Complex</span>
            <br />
            Businesses
          </h2>
          <p className={`text-lg max-w-xl mx-auto leading-relaxed ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
            Each system is tailored to your industry's operational realities—ensuring adoption, performance, and measurable outcomes.
          </p>
        </div>

        {/* Industry Grid with images */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {industries.map((industry, idx) => (
            <div
              key={idx}
              className={`group relative rounded-2xl overflow-hidden border transition-all duration-500 cursor-pointer ${
                isDark
                  ? 'border-white/6 hover:border-violet-400/20'
                  : 'border-black/6 hover:border-purple-500/20 shadow-sm hover:shadow-lg'
              }`}
              style={{ height: '320px' }}
            >
              {/* Background image */}
              <img
                src={industry.imageUrl}
                alt={industry.name}
                className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
              />
              {/* Overlay */}
              <div className="absolute inset-0" style={{
                background: isDark
                  ? 'linear-gradient(to top, #0E1116 0%, #0E1116/60 50%, #0E1116/20 100%)'
                  : 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 50%, rgba(0,0,0,0.1) 100%)'
              }}></div>
              {/* Hover overlay */}
              <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${
                isDark ? 'bg-gradient-to-t from-violet-500/15 to-transparent' : 'bg-gradient-to-t from-purple-500/15 to-transparent'
              }`}></div>

              {/* Content */}
              <div className="absolute bottom-0 left-0 right-0 p-6 flex flex-col gap-3">
                <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${
                  isDark ? 'bg-violet-400/15 border border-violet-400/20' : 'bg-purple-500/15 border border-purple-500/20'
                }`}>
                  <i className={`${industry.icon} text-base ${isDark ? 'text-violet-400' : 'text-purple-500'}`}></i>
                </div>
                <h3 className="text-lg font-bold text-white tracking-tight">{industry.name}</h3>
                <p className="text-sm text-gray-300 leading-relaxed">{industry.detail}</p>
              </div>

              {/* Top accent */}
              <div className={`absolute top-0 left-0 right-0 h-px opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${
                isDark ? 'bg-gradient-to-r from-transparent via-violet-400/40 to-transparent' : 'bg-gradient-to-r from-transparent via-purple-500/40 to-transparent'
              }`}></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}